import { Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon } from 'ol/geom';
import Feature from 'ol/Feature';

import { assertType } from 'cadenza/utils/custom-error';
import { isMultiGeometryType } from 'cadenza/map/geom/geometry-type';

import type { Geometry } from 'ol/geom';
import type { Type } from 'ol/geom/Geometry';

export function splitMultiGeometry (multiGeometry: MultiPoint | MultiLineString | MultiPolygon) {
  switch (multiGeometry.getType()) {
    case 'MultiPoint':
      return (multiGeometry as MultiPoint).getCoordinates().map(pointCoord => new Point(pointCoord));
    case 'MultiLineString':
      return (multiGeometry as MultiLineString).getCoordinates().map(lineStringCoords => new LineString(lineStringCoords));
    case 'MultiPolygon':
      return (multiGeometry as MultiPolygon).getCoordinates().map(polygonCoords => new Polygon(polygonCoords));
  }
  throw new Error();
}

export function combineToMultiPointFeature (features: Feature<Point>[]) {
  if (features.length === 0) {
    return new Feature();
  }
  return new Feature(combineToMultiPoint(features.map(feature => feature.getGeometry()!)));
}

export function combineToMultiLineStringFeature (features: Feature<LineString>[]) {
  if (features.length === 0) {
    return new Feature();
  }
  // we have to clone it, due to some weird ol behaviour if not
  return new Feature(combineToMultiLineString(features.map(feature => feature.getGeometry()!.clone())));
}

export function combineToMultiPolygonFeature (features: Feature<Polygon>[]) {
  if (features.length === 0) {
    return new Feature();
  }
  // we have to clone it, due to some weird ol behaviour if not
  return new Feature(combineToMultiPolygon(features.map(feature => feature.getGeometry()!.clone())));
}

export function combineToMultiPoint (points: Point[]) {
  const multiCoords = points.map(point => {
    return point.getCoordinates();
  });
  return new MultiPoint(multiCoords);
}

export function combineToMultiLineString (lineStrings: LineString[]) {
  return new MultiLineString(lineStrings);
}

export function combineToMultiPolygon (polygons: Polygon[]) {
  return new MultiPolygon(polygons);
}

export function assertMultiGeometry (geometry: Geometry): asserts geometry is MultiPoint | MultiLineString | MultiPolygon {
  assertType(geometry.getType(), isMultiGeometryType(geometry.getType()));
}

export function isPointLike (type: Type) {
  return type === 'Point' || type === 'MultiPoint';
}
export function isPolygonLike (type: Type) {
  return type === 'Polygon' || type === 'MultiPolygon';
}

export function toOLSingleGeometryType (geometryType: Type) {
  switch (geometryType) {
    case 'Point':
    case 'MultiPoint':
      return 'Point';
    case 'LineString':
    case 'MultiLineString':
      return 'LineString';
    case 'Polygon':
    case 'MultiPolygon':
      return 'Polygon';
    default:
      return geometryType;
  }
}
